/* eslint max-lines-per-function: ["error", { max: 225 }] -- applies to entire file */
/* eslint max-lines: ["error", { max: 361 }] -- applies to entire file */
import { differenceInDays } from "date-fns";
import uuid from "react-native-uuid";

import api, { CacheTagType } from "@/store/api";
import { buildUploadUrl } from "@/utils/api/file.util";
import { getAuthContext } from "@/utils/apiAuth.util";
import { isWeb } from "@/utils/platform.util";
import { convertAssetToFile } from "@/utils/shared.utils";
import {
  getInitialDateRangeForAllSMTDailyLogs,
  getNextDateRangeForAllSMTDailyLogs,
  parseDailyLogDate,
  populateMissingDailyLogs,
} from "@/utils/smt.util";

const SMTDailyLogsMaximumDaysToFetch = 90;

interface GetSMTDailyLogsPageParam {
  admitDate?: string;
  endDate: string;
  startDate: string;
}

interface GetSMTDailyLogEntryRequest {
  smtEntryId: string;
}

interface GetSMTDailyLogsRequest {
  admitDate?: string;
  patientId: string;
}

interface UploadSMTImageRequest {
  file: FilePickerAsset;
  smtEntryId: string;
}

interface UploadSMTImageResponse {
  fileScanStatus: string;
  fileServiceKey: string;
}

interface GetSMTConfigResponse {
  uiConfig: SMTUIConfig;
}
interface PatientSMTPermissions {
  isSMTEnabled: boolean;
  permissionType: SMTPermissionType;
}

interface PatientSMTPermissionsResponse extends Omit<
  PatientSMTPermissions,
  "isSMTEnabled"
> {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API returns 'smtEnabled' not 'isSMTEnabled'
  smtEnabled: boolean;
}

interface PatientSMTPermissionsRequest {
  patientId: string;
}

interface SupportPatientSMTPermissions {
  patientId: string;
  permissionType: SMTPermissionType;
}

interface SupportSMTPermissions {
  isSMTEnabled: boolean;
  permissions: SupportPatientSMTPermissions[];
}

interface SupportSMTPermissionsResponse {
  // API omits this field if it is an empty list
  permissions?: SupportPatientSMTPermissions[];
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API returns 'smtEnabled' not 'isSMTEnabled'
  smtEnabled: boolean;
}

interface SupportSMTPermissionRequest {
  supportId: string;
}

interface GetSMTSummaryRequest {
  endDate: string;
  patientId: string;
  startDate: string;
}

interface SMTSummaryEntry {
  date: string;
  hasEntry: boolean;
}

interface GetSMTSummaryResponse {
  entries: SMTSummaryEntry[];
  globalStreak: number;
}

const smtApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPatientSMTPermissions: build.query<
      PatientSMTPermissions,
      PatientSMTPermissionsRequest
    >({
      query: ({ patientId }) => ({
        method: "GET",
        url: `/assessments/family/patients/${patientId}/smt/permissions`,
      }),
      // rename smtEnabled to isSMTEnabled to meet repo naming conventions for booleans
      transformResponse: ({
        permissionType,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- API returns 'smtEnabled' not 'isSMTEnabled'
        smtEnabled,
      }: PatientSMTPermissionsResponse): PatientSMTPermissions => ({
        isSMTEnabled: smtEnabled,
        permissionType,
      }),
    }),
    getSMTConfig: build.query<GetSMTConfigResponse, void>({
      keepUnusedDataFor: Infinity,
      query: () => ({
        method: "GET",
        url: "/assessments/family/smt/config",
      }),
    }),
    getSMTDailyLogEntry: build.query<
      SMTDailyLogEntry,
      GetSMTDailyLogEntryRequest
    >({
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => [
        { id: arg.smtEntryId, type: CacheTagType.SMTEntry },
      ],
      query: ({ smtEntryId }) => ({
        method: "GET",
        url: `/assessments/family/smt/${smtEntryId}/meals`,
      }),
    }),
    getSMTDailyLogs: build.infiniteQuery<
      SMTDailyLog[],
      GetSMTDailyLogsRequest,
      GetSMTDailyLogsPageParam
    >({
      // on refetch (eg due to tag invalidation), will refetch ALL pages
      // this is in fact desired since, while "newer" entries would only be submitted today
      // they can be for any previous date or time, so all pages need refetch
      infiniteQueryOptions: {
        // eslint-disable-next-line max-params
        getNextPageParam: (
          _lastPage,
          _allPages,
          lastPageParam,
          allPagesParams,
          queryArg,
        ): GetSMTDailyLogsPageParam | undefined => {
          const latestDate = parseDailyLogDate(allPagesParams[0].endDate);
          const earliestDate = parseDailyLogDate(lastPageParam.startDate);
          const totalDayCount = differenceInDays(latestDate, earliestDate);

          const admitDate =
            queryArg.admitDate && parseDailyLogDate(queryArg.admitDate);
          const maximumDaysToFetch = admitDate
            ? differenceInDays(latestDate, admitDate)
            : SMTDailyLogsMaximumDaysToFetch;
          // prevent infinite API queries further and further back with some upper bound max # of days
          if (totalDayCount >= maximumDaysToFetch) {
            return undefined;
          }
          return getNextDateRangeForAllSMTDailyLogs({
            admitDate: queryArg.admitDate,
            endDate: lastPageParam.endDate,
            startDate: lastPageParam.startDate,
          });
        },
        // this can be overridden as a param when invoking the use*InfiniteQuery hook
        initialPageParam: getInitialDateRangeForAllSMTDailyLogs(),
      },
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => {
        return [{ id: arg.patientId, type: CacheTagType.SMTEntries }];
      },
      query: ({
        pageParam: { endDate, startDate },
        queryArg: { patientId },
      }) => {
        return {
          method: "GET",
          params: {
            endDate,
            startDate,
          },
          url: `/assessments/family/patients/${patientId}/smt/daily-logs`,
        };
      },
      serializeQueryArgs: ({ queryArgs }) => ({
        // admitDate is omitted from the cache key since it is only a client-side pagination limit and does not differentiate cache entries
        patientId: queryArgs.patientId,
      }),
      // eslint-disable-next-line max-params
      transformResponse: (
        response: SMTDailyLog[],
        _,
        args: { pageParam: GetSMTDailyLogsPageParam },
      ) => {
        const {
          pageParam: { endDate, startDate },
        } = args;

        return populateMissingDailyLogs({
          dailyLogs: response,
          endDate,
          startDate,
        });
      },
    }),
    getSMTDailyLogsByRange: build.query<
      SMTDailyLog[],
      GetSMTDailyLogsPageParam & GetSMTDailyLogsRequest
    >({
      query: ({ endDate, patientId, startDate }) => ({
        method: "GET",
        params: { endDate, startDate },
        url: `/assessments/family/patients/${patientId}/smt/daily-logs`,
      }),
      // eslint-disable-next-line max-params
      transformResponse: (
        response: SMTDailyLog[],
        _,
        { endDate, startDate },
      ) => {
        return populateMissingDailyLogs({
          dailyLogs: response,
          endDate,
          startDate,
        });
      },
    }),
    getSMTSummary: build.query<GetSMTSummaryResponse, GetSMTSummaryRequest>({
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => {
        return [{ id: arg.patientId, type: CacheTagType.SMTSummary }];
      },
      query: ({ endDate, patientId, startDate }) => ({
        method: "GET",
        params: {
          endDate,
          startDate,
        },
        url: `/assessments/family/patients/${patientId}/smt/summary`,
      }),
    }),
    getSupportSMTPermissions: build.query<
      SupportSMTPermissions,
      SupportSMTPermissionRequest
    >({
      query: ({ supportId }) => ({
        method: "GET",
        url: `/assessments/family/supports/${supportId}/smt/permissions`,
      }),
      // rename smtEnabled to isSMTEnabled to meet repo naming conventions for booleans
      transformResponse: ({
        permissions,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- API returns 'smtEnabled' not 'isSMTEnabled'
        smtEnabled,
      }: SupportSMTPermissionsResponse): SupportSMTPermissions => ({
        isSMTEnabled: smtEnabled,
        permissions: permissions ?? [],
      }),
    }),
    uploadSMTImage: build.mutation<
      UploadSMTImageResponse,
      UploadSMTImageRequest
    >({
      queryFn: async ({ file, smtEntryId }) => {
        const { token } = await getAuthContext();

        const uploadUrl = buildUploadUrl(
          `/assessments/family/smt/${smtEntryId}/images`,
        );

        const formData = new FormData();
        if (isWeb()) {
          // On web, FormData needs a real Blob/File
          const webFile = await convertAssetToFile({
            file,
            fileName: file.fileName,
          });
          formData.append("file", webFile);
        } else {
          // On native, RN's FormData has no real File; it streams the file from
          // `uri` at send time, using `name`/`type` as the multipart metadata.
          // @ts-expect-error -- TS expects a Blob, but we are able to pass an object as follows
          formData.append("file", {
            name: file.fileName,
            type: file.mimeType || "image/jpeg",
            uri: file.uri,
          });
        }
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          uniqueRequestId: `MyEquip:${uuid.v1()}`,
        };
        // Omit on web so the browser sets the multipart boundary itself.
        if (!isWeb()) {
          headers["Content-Type"] = "multipart/form-data";
        }

        const response = await fetch(uploadUrl, {
          body: formData,
          headers,
          method: "POST",
        });
        if (response.ok) {
          const data = await response.json();
          return { data };
        }
        const errorText = await response.text();
        return {
          error: { data: errorText, status: response.status },
        };
      },
    }),
  }),
});

const {
  useGetPatientSMTPermissionsQuery,
  useGetSMTConfigQuery,
  useGetSMTDailyLogEntryQuery,
  useGetSMTDailyLogsInfiniteQuery,
  useGetSMTSummaryQuery,
  useGetSupportSMTPermissionsQuery,
  useLazyGetSMTDailyLogsByRangeQuery,
  useUploadSMTImageMutation,
} = smtApi;

export {
  useGetPatientSMTPermissionsQuery,
  useGetSMTConfigQuery,
  useGetSMTDailyLogEntryQuery,
  useGetSMTDailyLogsInfiniteQuery,
  useGetSMTSummaryQuery,
  useGetSupportSMTPermissionsQuery,
  useLazyGetSMTDailyLogsByRangeQuery,
  useUploadSMTImageMutation,
};

export type {
  GetSMTDailyLogsPageParam,
  GetSMTSummaryResponse,
  PatientSMTPermissions,
  SMTSummaryEntry,
  SupportPatientSMTPermissions,
  UploadSMTImageResponse,
};

export default smtApi;
